import { useEffect, useRef } from 'react';
import { fetchRoute, fetchStaleLegs } from '../utils/routing';
import { useRoute } from '../context/RouteContext';
import { useAuth } from '../context/AuthContext';
import { rlog, rwarn, rnow } from '../utils/routingLog';

const TIMEOUT_MS = 30_000;  // covers Geoapify + Valhalla attempts + OSRM fallback
const DEBOUNCE_MS = 350;    // coalesce rapid waypoint edits into one request

export function useRouting() {
  const { state, dispatch } = useRoute();
  const { isSubscribed } = useAuth();
  const { current, profile, avoidOptions } = state;
  const abortRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const { waypoints, geometry, legs } = current;
    const valid = waypoints.filter(wp => wp.lat != null && wp.lng != null);

    if (valid.length < 2) {
      dispatch({ type: 'SET_GEOMETRY', payload: { geometry: null, distance: null, duration: null, legs: null } });
      return;
    }

    // A single moved waypoint invalidates only its 1–2 adjacent legs (see
    // UPDATE_WAYPOINT in RouteContext) rather than clearing geometry outright —
    // detect that case so we re-route just those legs instead of everything.
    const legsAligned = legs && legs.length === waypoints.length - 1;
    const legsStale = legsAligned && legs.some(l => l === null);

    // Geometry already known and (if tracked) every leg still fresh — skip routing.
    // Structural changes (add/remove/reorder, profile/avoid/curvy) clear geometry
    // in the reducer, so those still trigger a full route below.
    if (geometry !== null && !legsStale) {
      rlog('effect fired but geometry is fresh — nothing to do', { waypoints: waypoints.length });
      return;
    }

    if (geometry === null) {
      rlog(
        legs == null
          ? 'FULL reroute — no leg cache at all (structural edit, or the last route came from an engine with no per-leg data, e.g. OSRM)'
          : !legsAligned
            ? `FULL reroute — leg cache misaligned (${legs.length} legs cached vs ${waypoints.length - 1} expected)`
            : 'FULL reroute — geometry was cleared',
        { waypoints: waypoints.length },
      );
    } else {
      const staleIdx = legs.map((l, i) => (l === null ? i : null)).filter(i => i !== null);
      rlog(`PARTIAL reroute — only leg(s) [${staleIdx.join(', ')}] of ${legs.length} are stale, reusing the rest`);
    }

    // Show the routing state immediately; the actual fetch is debounced so a
    // burst of edits (dragging a waypoint, quick adds) fires only one request.
    dispatch({ type: 'SET_ROUTING', payload: true });
    dispatch({ type: 'SET_ROUTE_ERROR', payload: null });

    const debounce = setTimeout(() => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;
      const tStart = rnow();

      // Hard timeout — abort and surface a message if the server hangs.
      timeoutRef.current = setTimeout(() => {
        rwarn(`TIMEOUT after ${TIMEOUT_MS}ms — aborting and surfacing an error`);
        abortRef.current?.abort();
        dispatch({ type: 'SET_ROUTE_ERROR', payload: 'Routing timed out — the server may be slow. Try again.' });
        dispatch({ type: 'SET_ROUTING', payload: false });
      }, TIMEOUT_MS);

      const task = legsStale
        ? fetchStaleLegs(waypoints, legs, profile, avoidOptions, signal, isSubscribed)
            // A stale leg failing to resolve (e.g. no route found) falls back to
            // a full recompute rather than leaving the route half-broken.
            .then(result => {
              if (result) return result;
              rwarn('stale-leg reroute failed — falling back to a FULL reroute');
              return fetchRoute(valid, profile, avoidOptions, signal, isSubscribed);
            })
        : fetchRoute(valid, profile, avoidOptions, signal, isSubscribed);

      task
        .then(result => {
          clearTimeout(timeoutRef.current);
          rlog(`request settled in ${Math.round(rnow() - tStart)}ms (including ${DEBOUNCE_MS}ms debounce)`, result ? { distanceM: result.distance, legs: result.legs?.length ?? 'none' } : '(no result)');
          if (result) dispatch({ type: 'SET_GEOMETRY', payload: result });
          dispatch({ type: 'SET_ROUTING', payload: false });
        })
        .catch(err => {
          clearTimeout(timeoutRef.current);
          if (err.name === 'AbortError') { rlog('request aborted (superseded by a newer edit, or timed out)'); return; }
          rwarn(`request failed after ${Math.round(rnow() - tStart)}ms:`, err.message);
          dispatch({ type: 'SET_ROUTE_ERROR', payload: err.message });
          dispatch({ type: 'SET_ROUTING', payload: false });
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(debounce);
      clearTimeout(timeoutRef.current);
      abortRef.current?.abort();
    };
  }, [current.waypoints, profile, avoidOptions, isSubscribed]); // eslint-disable-line react-hooks/exhaustive-deps
}
